/**
 * AP 账单处理 Pipeline — 前端声明式定义
 *
 * 与后端 backend/ai_assistant/orchestration/ap_invoice_pipeline.py 对齐。
 * 看完此文件即可理解前端如何感知 Pipeline 的阶段、状态、进度和可用操作。
 *
 * 设计原则:
 *   1. 单一数据源 — 所有阶段/状态/UI行为映射从此文件派生
 *   2. 对齐后端 — stage_id、status 值与后端 PipelineDefinition 完全一致
 *   3. 前端自治 — 前端根据 IDS.status 推算当前阶段和可用操作，不需要额外 API
 */

import { idsIsTerminal } from "../constants";

// =============================================================================
// Pipeline 阶段定义（有序数组，索引即顺序）
// =============================================================================

export const PIPELINE_STAGES = [
    {
        stageId: "ocr",
        displayName: "OCR 识别",
        shortName: "OCR",
        icon: "ScanOutlined",
        statuses: {
            inProgress: "OCR_IN_PROGRESS",
            completed: "OCR_COMPLETED",
            failed: "OCR_FAILED",
        },
    },
    {
        stageId: "parser",
        displayName: "制表校验",
        shortName: "制表校验",
        icon: "FileSearchOutlined",
        statuses: {
            inProgress: "STRUCTURE_IN_PROGRESS",
            completed: "STRUCTURE_COMPLETED",
            failed: "STRUCTURE_FAILED",
            blocked: "STRUCTURE_BLOCKED", // 门控: 金额平衡校验不通过
        },
    },
    {
        stageId: "allocation",
        displayName: "分摊航班",
        shortName: "分摊航班",
        icon: "ApartmentOutlined",
        statuses: {
            inProgress: "ALLOCATION_IN_PROGRESS",
            completed: "ALLOCATION_COMPLETED",
            failed: "ALLOCATION_FAILED",
            blocked: "ALLOCATION_BLOCKED", // 门控: 一致性共识全不通过
        },
    },
    {
        stageId: "validation",
        displayName: "验证航班",
        shortName: "验证航班",
        icon: "SafetyCertificateOutlined",
        statuses: {
            inProgress: "VALIDATION_IN_PROGRESS",
            completed: "VALIDATION_COMPLETED",
            failed: "VALIDATION_COMPLETED", // 验证"失败"≠阶段失败
        },
    },
    {
        stageId: "batch_validation",
        displayName: "整批核查",
        shortName: "整批核查",
        icon: "AuditOutlined",
        statuses: {
            inProgress: "BATCH_VALIDATION_IN_PROGRESS",
            completed: "BATCH_VALIDATION_COMPLETED",
            failed: "BATCH_VALIDATION_FAILED",
        },
    },
    // Phase 2 预留
    // {
    //     stageId: "order_recon",
    //     displayName: "订单核对",
    //     shortName: "核对",
    //     icon: "AuditOutlined",
    //     statuses: {
    //         inProgress: "ORDER_RECON_IN_PROGRESS",
    //         completed: "ORDER_RECON_COMPLETED",
    //         failed: "ORDER_RECON_FAILED",
    //     },
    // },
];

export const TOTAL_STAGES = PIPELINE_STAGES.length;

// =============================================================================
// 构建查找索引（性能优化 — 避免每次渲染遍历）
// =============================================================================

/** status → { stageIndex, stageId, phase: "inProgress"|"completed"|"failed"|"blocked" } */
const _statusToStageMap = {};
PIPELINE_STAGES.forEach((stage, index) => {
    const { inProgress, completed, failed, blocked } = stage.statuses;
    _statusToStageMap[inProgress] = { stageIndex: index, stageId: stage.stageId, phase: "inProgress" };
    _statusToStageMap[completed] = { stageIndex: index, stageId: stage.stageId, phase: "completed" };
    if (failed !== completed) {
        _statusToStageMap[failed] = { stageIndex: index, stageId: stage.stageId, phase: "failed" };
    }
    if (blocked) {
        _statusToStageMap[blocked] = { stageIndex: index, stageId: stage.stageId, phase: "blocked" };
    }
});
// 特殊状态
_statusToStageMap["RECEIVED"] = { stageIndex: -1, stageId: null, phase: "pending" };
_statusToStageMap["CONFIRMED"] = { stageIndex: TOTAL_STAGES, stageId: null, phase: "terminal" };
_statusToStageMap["REJECTED"] = { stageIndex: TOTAL_STAGES, stageId: null, phase: "terminal" };
_statusToStageMap["VOIDED"] = { stageIndex: TOTAL_STAGES, stageId: null, phase: "terminal" };

// =============================================================================
// 核心工具函数
// =============================================================================

/**
 * 从 IDS status 解析 Pipeline 进度
 *
 * @param {string} status - IDS 后台状态
 * @returns {{
 *   stageIndex: number,     // 当前所在阶段索引（-1=初始, 0~N-1=阶段内, N=终态）
 *   stageId: string|null,   // 当前阶段ID
 *   phase: string,          // "pending"|"inProgress"|"completed"|"failed"|"blocked"|"terminal"
 *   completedStages: number,// 已完成的阶段数（用于进度条百分比）
 *   currentStage: object|null, // 当前阶段定义
 * }}
 */
export function parseProgress(status) {
    const mapping = _statusToStageMap[status];
    if (!mapping) {
        return { stageIndex: -1, stageId: null, phase: "unknown", completedStages: 0, currentStage: null };
    }

    let completedStages = 0;
    if (mapping.phase === "completed" || mapping.phase === "terminal") {
        completedStages = mapping.stageIndex + 1;
    } else if (mapping.phase === "inProgress") {
        completedStages = mapping.stageIndex;
    } else if (mapping.phase === "failed" || mapping.phase === "blocked") {
        completedStages = mapping.stageIndex;
    }

    // 限制范围
    completedStages = Math.max(0, Math.min(completedStages, TOTAL_STAGES));

    return {
        stageIndex: mapping.stageIndex,
        stageId: mapping.stageId,
        phase: mapping.phase,
        completedStages,
        currentStage: mapping.stageId ? PIPELINE_STAGES[mapping.stageIndex] : null,
    };
}

/**
 * 获取进度百分比（0~100）
 */
export function getProgressPercent(status) {
    const { completedStages, phase } = parseProgress(status);
    if (phase === "terminal") return 100;
    return Math.round((completedStages / TOTAL_STAGES) * 100);
}

/**
 * 获取当前阶段的可读名称
 */
export function getStageLabel(status) {
    if (!status) return "—";
    if (status === "RECEIVED") return "待处理";
    if (status === "CONFIRMED") return "已确认";
    if (status === "REJECTED") return "已拒绝";
    if (status === "VOIDED") return "已作废";

    const { currentStage, phase } = parseProgress(status);
    if (phase === "blocked") {
        return `${currentStage?.shortName || ""}受阻`;
    }
    return currentStage?.shortName || status;
}

/** 阶段内部 phase 对应的子标签和颜色 */
const PHASE_DISPLAY = {
    inProgress: { subLabel: "处理中", color: "#1890ff", dotColor: "processing" },
    completed:  { subLabel: "已完成", color: "#52c41a", dotColor: "success" },
    failed:     { subLabel: "失败",   color: "#ff4d4f", dotColor: "error" },
    blocked:    { subLabel: "受阻",   color: "#fa8c16", dotColor: "warning" },
    pending:    { subLabel: "",       color: "#d9d9d9", dotColor: "default" },
    terminal:   { subLabel: "",       color: "#52c41a", dotColor: "success" },
    unknown:    { subLabel: "",       color: "#d9d9d9", dotColor: "default" },
};

/**
 * 获取阶段的完整展示信息（阶段名 + 阶段内状态）
 *
 * @param {string} status - IDS 后台状态
 * @returns {{ label: string, subLabel: string, color: string, dotColor: string, phase: string }}
 */
export function getStageInfo(status) {
    if (!status || status === "RECEIVED") {
        return { label: "待处理", subLabel: "", color: "#d9d9d9", dotColor: "default", phase: "pending" };
    }
    if (status === "CONFIRMED") {
        return { label: "已确认", subLabel: "", color: "#52c41a", dotColor: "success", phase: "terminal" };
    }
    if (status === "REJECTED") {
        return { label: "已拒绝", subLabel: "", color: "#ff4d4f", dotColor: "error", phase: "terminal" };
    }
    if (status === "VOIDED") {
        return { label: "已作废", subLabel: "", color: "#8c8c8c", dotColor: "default", phase: "terminal" };
    }

    const { currentStage, phase } = parseProgress(status);
    const phaseInfo = PHASE_DISPLAY[phase] || PHASE_DISPLAY.unknown;
    const stageName = currentStage?.shortName || "—";

    return {
        label: stageName,
        subLabel: phaseInfo.subLabel,
        color: phaseInfo.color,
        dotColor: phaseInfo.dotColor,
        phase,
    };
}

// =============================================================================
// IDS 显示状态 (Display Status — 对齐 IDS 状态机的用户可见态)
// =============================================================================

/**
 * 从后台 pipeline 状态 → 用户可见状态
 *
 * 关键映射说明:
 *   VALIDATION_COMPLETED → REVIEW（验证航班完成即可待审核）
 *   BATCH_VALIDATION_COMPLETED → REVIEW（整批核查已跳过/视为完成）
 *   BATCH_VALIDATION_FAILED → REVIEW（legacy/advisory 结果，不阻塞审核）
 *   *_BLOCKED → BLOCKED（Agent 成功但门控不通过，需人工干预）
 *
 * @param {string} backendStatus
 * @returns {"PENDING"|"PROCESSING"|"FAILED"|"BLOCKED"|"REVIEW"|"CONFIRMED"|"REJECTED"|"VOIDED"}
 */
export function getDisplayStatus(backendStatus) {
    if (!backendStatus) return "PENDING";
    if (backendStatus === "RECEIVED") return "PENDING";
    if (backendStatus === "CONFIRMED") return "CONFIRMED";
    if (backendStatus === "REJECTED") return "REJECTED";
    if (backendStatus === "VOIDED") return "VOIDED";
    if (["VALIDATION_COMPLETED", "BATCH_VALIDATION_COMPLETED", "BATCH_VALIDATION_FAILED"].includes(backendStatus)) return "REVIEW";
    if (backendStatus.includes("BLOCKED")) return "BLOCKED";
    if (backendStatus.includes("FAILED")) return "FAILED";
    if (backendStatus.includes("IN_PROGRESS")) return "PROCESSING";
    // 其他 COMPLETED（OCR_COMPLETED, STRUCTURE_COMPLETED, ALLOCATION_COMPLETED）仍在流水线中间。
    return "PROCESSING";
}

/** 用户可见状态的标签和颜色 */
export const DISPLAY_STATUS_CONFIG = {
    PENDING: { label: "待处理", color: "default", tagColor: "#d9d9d9" },
    PROCESSING: { label: "处理中", color: "processing", tagColor: "#1890ff" },
    FAILED: { label: "处理失败", color: "error", tagColor: "#ff4d4f" },
    BLOCKED: { label: "已受阻", color: "warning", tagColor: "#fa8c16" },
    REVIEW: { label: "待审核", color: "warning", tagColor: "#faad14" },
    CONFIRMED: { label: "已确认", color: "success", tagColor: "#52c41a" },
    REJECTED: { label: "已拒绝", color: "error", tagColor: "#ff4d4f" },
    VOIDED: { label: "已作废", color: "default", tagColor: "#8c8c8c" },
};

function buildPerIdsRetryActions({ reverse = false } = {}) {
    const actions = reverse ? [...PER_IDS_RETRY_STAGE_CONFIG].reverse() : PER_IDS_RETRY_STAGE_CONFIG;
    return actions.map((action) => ({
        key: `rerun_${action.stageId}`,
        label: `从${PIPELINE_STAGES.find((stage) => stage.stageId === action.stageId)?.shortName || action.stageId}重试`,
        stageId: action.stageId,
        icon: "ReloadOutlined",
    }));
}

// =============================================================================
// Pipeline 操作（根据 IDS 当前状态推断可执行的操作）
// =============================================================================

/**
 * 获取 IDS 可执行的 Pipeline 操作列表
 *
 * @param {string} status - IDS 后台状态
 * @returns {Array<{key: string, label: string, stageId: string, danger?: boolean, confirm?: string}>}
 */
export function getAvailableActions(status) {
    const displayStatus = getDisplayStatus(status);
    const { stageId } = parseProgress(status);
    const actions = [];

    if (displayStatus === "PENDING") {
        actions.push({
            key: "start",
            label: "开始处理",
            stageId: "ocr",
            icon: "PlayCircleOutlined",
        });
    }

    if (displayStatus === "FAILED") {
        if (stageId === "batch_validation") {
            actions.push(...buildPerIdsRetryActions({ reverse: true }));
        } else {
            actions.push({
                key: "retry_current",
                label: `从${getStageLabel(status)}重试`,
                stageId: stageId,
                icon: "ReloadOutlined",
            });
            actions.push({
                key: "retry_from_start",
                label: "从头开始",
                stageId: "ocr",
                icon: "UndoOutlined",
            });
        }
    }

    if (displayStatus === "BLOCKED") {
        // STRUCTURE_BLOCKED 特有：用户修改数据后可直接推进到分摊阶段，
        // 后端在推进前会重新执行结构完整性门禁和金额平衡门禁（doc 26 §6.3 item 7）。
        if (status === "STRUCTURE_BLOCKED") {
            actions.push({
                key: "continue_next",
                label: "继续下一步",
                stageId: "allocation",
            });
        }
        // 受阻: 用户可以修改后从当前阶段重试，或回退到更早阶段
        actions.push({
            key: "retry_current",
            label: `修改后重试${getStageLabel(status).replace("受阻", "")}`,
            stageId: stageId,
            icon: "ReloadOutlined",
        });
        // 回退到上一阶段
        const stageIndex = parseProgress(status).stageIndex;
        if (stageIndex > 0) {
            const prevStage = PIPELINE_STAGES[stageIndex - 1];
            actions.push({
                key: "retry_prev_stage",
                label: `回退到${prevStage.shortName}`,
                stageId: prevStage.stageId,
                icon: "RollbackOutlined",
            });
        }
        actions.push({
            key: "retry_from_start",
            label: "从头开始",
            stageId: "ocr",
            icon: "UndoOutlined",
        });
    }

    if (displayStatus === "PROCESSING") {
        const { phase } = parseProgress(status);
        if (phase === "inProgress") {
            // 只有 Agent 真正在运行时（*_IN_PROGRESS）才提供"停止处理"
            actions.push({
                key: "stop",
                label: "停止处理",
                danger: true,
                icon: "StopOutlined",
                confirm: "确认停止当前处理？",
            });
        } else if (phase === "completed") {
            // 中间阶段已完成但流水线未自动继续（不应该出现，但防御性处理）
            // 提供"继续处理"让用户手动推进或从当前阶段重试
            actions.push({
                key: "retry_current",
                label: `从${getStageLabel(status)}继续`,
                stageId: stageId,
                icon: "PlayCircleOutlined",
            });
        }
    }

    if (displayStatus === "REVIEW") {
        // 从特定阶段重新处理
    // 注意：batch_validation 是 per-batch 阶段，不能从单个 IDS 触发
    // 整批核查的入口在 InvoiceListTab 工具栏和 ReconciliationHeader 的重置下拉菜单
        PIPELINE_STAGES.forEach((stage) => {
            if (stage.stageId === "batch_validation") return; // 跳过 per-batch 阶段
            actions.push({
                key: `rerun_${stage.stageId}`,
                label: `重新${stage.shortName}`,
                stageId: stage.stageId,
                icon: "ReloadOutlined",
            });
        });
    }

    return actions;
}

/**
 * 获取批量重试的菜单项
 *
 * @param {Array} selectedItems - 选中的 IDS 列表
 * @returns {Array} antd Menu items
 */
export function getBatchRetryMenuItems(selectedItems) {
    const items = [
        {
            key: "auto",
            label: "从失败阶段重试（智能）",
        },
        { type: "divider" },
    ];

    PIPELINE_STAGES.forEach((stage) => {
        // batch_validation 是批次级操作，不能通过 per-IDS 的批量接口触发
        if (stage.stageId === "batch_validation") return;
        items.push({
            key: stage.stageId,
            label: `从${stage.shortName}重试`,
        });
    });

    return items;
}

/**
 * 推断智能重试应使用的阶段
 *
 * @param {string} status - IDS 后台状态
 * @returns {string} 应该重试的阶段 stageId
 */
export function inferRetryStage(status) {
    const { stageId, phase } = parseProgress(status);

    if (stageId === "batch_validation") {
        return "validation";
    }

    if ((phase === "failed" || phase === "blocked") && stageId) {
        return stageId;
    }
    if (status === "RECEIVED") return "ocr";

    // 其他情况（如 COMPLETED 状态的手动重试）→ 从当前阶段开始
    return stageId || "ocr";
}

const PER_IDS_RETRY_STAGE_CONFIG = [
    {
        key: "ocr",
        stageId: "ocr",
        label: "重新 OCR 识别",
        minCompletedStages: 0,
    },
    {
        key: "parser",
        stageId: "parser",
        label: "重新制表校验",
        minCompletedStages: 1,
    },
    {
        key: "allocation",
        stageId: "allocation",
        label: "重新分摊航班",
        minCompletedStages: 2,
    },
    {
        key: "validation",
        stageId: "validation",
        label: "重新验证航班",
        minCompletedStages: 3,
    },
];

export function getPerIdsPipelineActions(status) {
    const { completedStages, phase } = parseProgress(status);

    if (phase === "terminal") {
        return [];
    }

    if (phase === "inProgress") {
        return [
            {
                key: "stop",
                label: "强制停止",
                danger: true,
            },
        ];
    }

    return PER_IDS_RETRY_STAGE_CONFIG.filter(
        (action) => completedStages >= action.minCompletedStages,
    );
}

export function hasPerIdsPipelineActions(status) {
    return getPerIdsPipelineActions(status).length > 0;
}

// =============================================================================
// 筛选分类 (Pipeline Stage — 按阶段筛选)
//
// 替代原 STATUS_FILTER_CATEGORIES（按 Display Status 筛选）。
// 用户实际关心的是"DS 当前处于哪个阶段"，而非抽象的显示状态。
// 所有归属判断统一走 parseProgress()，与阶段定义保持单一数据源。
// =============================================================================

export const STAGE_FILTER_CATEGORIES = {
    all: { label: "全部", filter: () => true },
    pending: {
        label: "待处理",
        color: "#8c8c8c",
        filter: (s) => {
            const { phase } = parseProgress(s);
            return phase === "pending" || phase === "unknown";
        },
    },
    ocr: {
        label: "OCR",
        color: "#1890ff",
        filter: (s) => parseProgress(s).stageId === "ocr",
    },
    parser: {
        label: "制表校验",
        color: "#722ed1",
        filter: (s) => parseProgress(s).stageId === "parser",
    },
    allocation: {
        label: "分摊航班",
        color: "#13c2c2",
        filter: (s) => parseProgress(s).stageId === "allocation",
    },
    validation: {
        label: "验证航班",
        color: "#fa8c16",
        filter: (s) => parseProgress(s).stageId === "validation",
    },
    batch_validation: {
        label: "整批核查",
        color: "#eb2f96",
        filter: (s) => parseProgress(s).stageId === "batch_validation",
    },
    terminal: {
        label: "已终结",
        color: "#52c41a",
        filter: (s) => parseProgress(s).phase === "terminal",
    },
};

// =============================================================================
// 状态快捷筛选 (Display Status 维度 — 与阶段筛选正交组合)
//
// 阶段筛选回答"在哪一步"，状态筛选回答"这步怎么了"。
// 二者以 AND 逻辑组合，满足"找到 OCR 阶段失败的" 等复合场景。
// =============================================================================

export const STATUS_QUICK_FILTERS = {
    all: { label: "不限", filter: () => true },
    processing: {
        label: "处理中",
        color: "#1890ff",
        filter: (s) => getDisplayStatus(s) === "PROCESSING",
    },
    failed: {
        label: "失败",
        color: "#ff4d4f",
        filter: (s) => {
            const ds = getDisplayStatus(s);
            return ds === "FAILED" || ds === "BLOCKED";
        },
    },
    review: {
        label: "待审核",
        color: "#faad14",
        filter: (s) => getDisplayStatus(s) === "REVIEW",
    },
    voided: {
        label: "已作废",
        color: "#8c8c8c",
        filter: (s) => s === "VOIDED",
    },
};

// =============================================================================
// 整批核查就绪检查
// =============================================================================

/** 允许参与整批核查的 IDS 后端状态（验证航班完毕之后） */
const BATCH_VALIDATION_ELIGIBLE_STATUSES = new Set([
    "VALIDATION_COMPLETED",
    "BATCH_VALIDATION_COMPLETED",
    "BATCH_VALIDATION_FAILED",
]);

/**
 * 检查单个 IDS 是否满足整批核查条件
 *
 * @param {string} status - IDS 后端状态
 * @returns {boolean}
 */
export function isBatchValidationEligible(status) {
    return BATCH_VALIDATION_ELIGIBLE_STATUSES.has(status);
}

/**
 * 检查整个批次是否可以触发整批核查
 *
 * 规则：所有活跃 IDS 都必须处于验证航班完毕之后的状态（VALIDATION_COMPLETED 或更后面）。
 * 终态 IDS 已退出业务流水线，不参与就绪判断。
 *
 * @param {Array<{status: string, original_filename?: string}>} idsList - 批次内所有 IDS
 * @returns {{ ready: boolean, total: number, eligible: number, ineligible: Array<{filename: string, status: string}> }}
 */
export function checkBatchValidationReadiness(idsList) {
    if (!idsList || idsList.length === 0) {
        return { ready: false, total: 0, eligible: 0, ineligible: [] };
    }

    const ineligible = [];
    let eligible = 0;

    idsList.forEach((ids) => {
        if (idsIsTerminal(ids.status)) {
            return;
        }
        if (isBatchValidationEligible(ids.status)) {
            eligible++;
        } else {
            ineligible.push({
                filename: ids.original_filename || ids.id,
                status: getStageLabel(ids.status),
            });
        }
    });

    return {
        ready: eligible > 0 && ineligible.length === 0,
        total: eligible + ineligible.length,
        eligible,
        ineligible,
    };
}
